import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from "react";
import { toast } from "react-toastify";
import moment from "moment";

import { getMLAnalyticsSession, mlAnalyticsGenerate } from "../../../actions/aiAnalytics";
import useAbortController from "../../Common/hooks/useAbortController";
import { convertUnit8ArrayToJson, getFilesFromS3, getUser } from "../../../utils/helpers";
import AIAnalyticsPromptComposer from "./AIAnalyticsPromptComposer";
import AIAnalyticsPromptResultPanel from "./AIAnalyticsPromptResultPanel";
import AIAnalyticsPromptSidebar from "./AIAnalyticsPromptSidebar";
import { DEFAULT_DATE_FORMAT, generateSessionId, normalizePromptStatus } from "./utils";

import "./AIAnalyticsPromptWorkspace.css";

/** Normalizes query result JSON into query_summary and data rows. @param {Array|Object} data - raw query result payload */
const normalizeQueryResultData = (data) => {
  if (Array.isArray(data)) {
    return { query_summary: "Query Result", data };
  }
  return {
    query_summary: data?.query_summary || "Query Result",
    data: data?.data || [],
  };
};

/** Finds the first file entry matching a given type. @param {Array} files - API file descriptors; @param {string} type - file type such as plot or query_result */
const getFileByType = (files, type) => (files || []).find((file) => file.type === type);

/** Resolves the query result file, preferring query_result over legacy data type. @param {Array} files - API file descriptors */
const getQueryResultFile = (files) => (
  getFileByType(files, "query_result") || getFileByType(files, "data")
);

/** Returns true when generate API succeeded with status 1. @param {Object} response - mlAnalyticsGenerate response */
const isGenerateResponseSuccess = (response) => (
  response?.success && Number(response?.status) === 1
);

/** Builds a normalized API error object for prompt state. @param {Object} response - API error response; @param {string} [fallbackType=UNKNOWN_ERROR] - default error_type */
const buildPromptError = (response, fallbackType = "UNKNOWN_ERROR") => ({
  error_type: response?.error_type || fallbackType,
  error_message: response?.error_message || response?.message || "Something went wrong. Update the prompt or retry.",
});

/** Builds an error object when S3 result files fail to load. @param {string} message - user-facing error message */
const buildFileLoadError = (message) => ({
  error_type: "FILE_LOAD_ERROR",
  error_message: message,
});

const emptySessionResult = {
  sessionId: null,
  sessionTitle: "",
  prompts: [],
};

/** Builds a normalized prompt result object for UI state. @param {Object} [options] - prompt_id, prompt, timestamp, user_name, status, error, plotData, queryResultData, queryResultFile */
const buildPromptResultItem = ({
  prompt_id: promptId = null,
  prompt = "",
  timestamp = null,
  user_name: userName = "",
  status = null,
  error = null,
  plotData = null,
  queryResultData = null,
  queryResultFile = null,
} = {}) => ({
  prompt_id: promptId,
  prompt,
  timestamp,
  user_name: userName,
  status: normalizePromptStatus(status),
  error,
  plotData,
  queryResultData,
  queryResultFile,
});

/** Builds a failed prompt result from base prompt fields and an error message. @param {Object} prompt - base prompt fields; @param {string} message - error message; @param {string} [errorType=ML_FAILED_STATUS] - error_type value */
const buildPromptErrorState = (prompt, message, errorType = "ML_FAILED_STATUS") => (
  buildPromptResultItem({
    ...prompt,
    status: "failed",
    error: {
      error_type: errorType,
      error_message: message,
    },
  })
);

/** Builds a sidebar prompt key from session and prompt ids. @param {string} sessionId - session id; @param {string|number} promptId - prompt id */
const buildPromptKey = (sessionId, promptId) => `${String(sessionId)}-${String(promptId)}`;

/** Returns true when a sidebar prompt key exists in the session history list. @param {string|null} promptKey - sessionId-promptId key; @param {Array} sessionList - session history from API */
const isPromptKeyInSessions = (promptKey, sessionList) => {
  if (!promptKey) return false;
  return sessionList.some((session) => (
    (session.prompts || []).some(
      (prompt) => buildPromptKey(session.session_id, prompt.prompt_id) === promptKey,
    )
  ));
};

/** Main workspace with composer, result panel, and sidebar. @param {Array} sessions - session history; @param {boolean} isSessionsLoading - sidebar loading state; @param {string} sessionId - active session id; @param {Function} onWorkspaceStateChange - emits result view state; @param {Function} onPromptsRefetch - refreshes sidebar after generate; @param {React.Ref} ref - exposes startNewPrompt */
function AIAnalyticsPromptWorkspace({
  sessions = [], isSessionsLoading = false, sessionId, onWorkspaceStateChange, onPromptsRefetch,
}, ref) {
  const [promptText, setPromptText] = useState("");
  const [isResultView, setIsResultView] = useState(false);
  const [isPromptLoading, setIsPromptLoading] = useState(false);
  const [sessionResult, setSessionResult] = useState(emptySessionResult);
  const [activeSessionId, setActiveSessionId] = useState(sessionId);
  const [selectedPromptKey, setSelectedPromptKey] = useState(null);
  const isMountedRef = useRef(true);
  const skipSessionValidationRef = useRef(false);
  const activeLoadRequestRef = useRef(0);
  const { setNewAbortController, abortController } = useAbortController();

  /** Refreshes the sidebar and skips the next selected-prompt validation pass. @returns {Promise<void>} */
  const refetchSidebarPrompts = useCallback(async () => {
    skipSessionValidationRef.current = true;
    await onPromptsRefetch();
  }, [onPromptsRefetch]);

  useEffect(() => {
    if (sessionId) {
      setActiveSessionId(sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    if (onWorkspaceStateChange) {
      onWorkspaceStateChange({ isResultView, selectedPromptKey });
    }
  }, [isResultView, selectedPromptKey, onWorkspaceStateChange]);

  useEffect(() => {
    if (!isResultView || !selectedPromptKey || isSessionsLoading || isPromptLoading) return;

    if (skipSessionValidationRef.current) {
      skipSessionValidationRef.current = false;
      return;
    }

    if (!isPromptKeyInSessions(selectedPromptKey, sessions)) {
      setSelectedPromptKey(null);
      setIsResultView(false);
      setIsPromptLoading(false);
      setSessionResult(emptySessionResult);
    }
  }, [sessions, isSessionsLoading, isResultView, selectedPromptKey, isPromptLoading]);

  /** Loads plot and query result files from S3 for a prompt. @param {Array} files - API file descriptors with type, path, bucket, and region; @param {AbortSignal} [signal] - abort signal */
  const fetchPromptFiles = async (files, signal = null) => {
    if (signal?.aborted) {
      throw Object.assign(new Error("Canceled"), { canceled: true });
    }

    const plotFileData = getFileByType(files, "plot");
    const queryResultFileData = getQueryResultFile(files);

    const fileEntries = [
      { type: "plot", file: plotFileData },
      { type: "query", file: queryResultFileData },
    ].filter(({ file }) => file?.path);

    let plotData = null;
    let queryResultData = null;

    if (!fileEntries.length) {
      return { plotData, queryResultData, queryResultFile: queryResultFileData };
    }

    const responses = await getFilesFromS3(
      fileEntries.map(({ file }) => ({
        bucket: file.bucket,
        region: file.region,
        path: file.path,
      })),
      false,
      true,
      "application/json",
      signal,
    );

    if (signal?.aborted) {
      throw Object.assign(new Error("Canceled"), { canceled: true });
    }

    fileEntries.forEach(({ type, file }) => {
      const response = responses[file.path];
      if (response?.aborted) {
        throw Object.assign(new Error("Canceled"), { canceled: true });
      }
      if (!response?.success) {
        throw new Error(type === "plot" ? "Failed to load plot data." : "Failed to load query result data.");
      }

      const jsonData = convertUnit8ArrayToJson(response.data);
      if (type === "plot") {
        plotData = jsonData;
      } else {
        queryResultData = normalizeQueryResultData(jsonData);
      }
    });

    return { plotData, queryResultData, queryResultFile: queryResultFileData };
  };

  /** Resolves a session prompt into UI state by status and S3 files. @param {string} currentSessionId - session id; @param {Object} prompt - prompt object from getMLAnalyticsSession; @param {AbortSignal} [signal] - abort signal */
  const resolvePromptResult = async (currentSessionId, prompt, signal = null) => {
    const basePrompt = {
      prompt_id: prompt.prompt_id,
      prompt: prompt.prompt || "",
      timestamp: prompt.timestamp || null,
      user_name: prompt.user_name || "",
      status: normalizePromptStatus(prompt.status),
    };

    const normalizedStatus = normalizePromptStatus(prompt.status);

    if (normalizedStatus === "failed") {
      return buildPromptErrorState(
        basePrompt,
        prompt.error_message || "This prompt failed to generate a result.",
      );
    }

    if (normalizedStatus === "pending") {
      return buildPromptResultItem({
        ...basePrompt,
        status: "pending",
        error: {
          error_type: "PENDING",
          error_message: "This prompt is still being processed.",
        },
      });
    }

    try {
      const nextResult = await fetchPromptFiles(prompt.files, signal);
      if (signal?.aborted) {
        throw Object.assign(new Error("Canceled"), { canceled: true });
      }
      return buildPromptResultItem({
        ...basePrompt,
        plotData: nextResult.plotData,
        queryResultData: nextResult.queryResultData,
        queryResultFile: nextResult.queryResultFile,
      });
    } catch (error) {
      if (error?.canceled || signal?.aborted) {
        throw error;
      }
      return buildPromptResultItem({
        ...basePrompt,
        error: buildFileLoadError(error.message || "Unable to load result files."),
      });
    }
  };

  /** Submits a prompt to mlAnalyticsGenerate and updates result state. @param {string} nextPrompt - prompt text; @param {string} [type] - new or retry generates a new session id */
  const submitPrompt = async (nextPrompt, type) => {
    let sessionIdPayload = activeSessionId;
    if (type === "new" || type === "retry") {
      sessionIdPayload = generateSessionId();
      setActiveSessionId(sessionIdPayload);
    }

    const currentUser = getUser();
    const controller = setNewAbortController();
    setSessionResult({
      sessionId: sessionIdPayload,
      sessionTitle: "",
      prompts: [buildPromptResultItem({
        prompt: nextPrompt,
        timestamp: moment().format("YYYY-MM-DD HH:mm:ss"),
        user_name: currentUser?.username || "",
        status: "pending",
      })],
    });
    setIsResultView(true);
    setIsPromptLoading(true);

    const response = await mlAnalyticsGenerate(sessionIdPayload, nextPrompt, controller.signal);
    if (!isMountedRef.current) return;

    // Refresh the list if the api cancelled
    if (response.canceled) {
      refetchSidebarPrompts();
      setIsPromptLoading(false);
      return;
    }

    const resolvedSessionId = response.session_id || sessionIdPayload;
    if (response.session_id) {
      setActiveSessionId(resolvedSessionId);
    }

    const basePrompt = {
      prompt_id: response.prompt_id || null,
      prompt: nextPrompt,
      timestamp: response.timestamp || null,
      user_name: response.user_name || currentUser?.username || "",
      status: response.status,
    };

    // If not success
    if (!response.success) {
      setSessionResult({
        sessionId: resolvedSessionId,
        sessionTitle: "",
        prompts: [buildPromptResultItem({
          ...basePrompt,
          status: normalizePromptStatus(response.status) || "failed",
          error: buildPromptError(response),
        })],
      });
      setIsPromptLoading(false);
      return;
    }

    // refetch the prompts list after the generate api responds
    if (basePrompt.prompt_id) {
      setSelectedPromptKey(buildPromptKey(resolvedSessionId, basePrompt.prompt_id));
    }
    refetchSidebarPrompts();

    // if success but api status != 1
    if (!isGenerateResponseSuccess(response)) {
      setSessionResult({
        sessionId: resolvedSessionId,
        sessionTitle: "",
        prompts: [buildPromptResultItem({
          ...basePrompt,
          status: normalizePromptStatus(response.status),
          error: buildPromptError(response, "ML_FAILED_STATUS"),
        })],
      });
      setIsPromptLoading(false);
      return;
    }

    // Response is success and status == 1
    try {
      const nextResult = await fetchPromptFiles(response.files, controller.signal);
      if (!isMountedRef.current || controller.signal.aborted) return;
      setSessionResult({
        sessionId: resolvedSessionId,
        sessionTitle: "",
        prompts: [buildPromptResultItem({
          ...basePrompt,
          status: normalizePromptStatus(response.status),
          plotData: nextResult.plotData,
          queryResultData: nextResult.queryResultData,
          queryResultFile: nextResult.queryResultFile,
        })],
      });
      setIsPromptLoading(false);
    } catch (error) {
      if (!isMountedRef.current || error?.canceled || controller.signal.aborted) return;
      setSessionResult({
        sessionId: resolvedSessionId,
        sessionTitle: "",
        prompts: [buildPromptResultItem({
          ...basePrompt,
          status: normalizePromptStatus(response.status) || "failed",
          error: buildFileLoadError(error.message || "Unable to load result files. Update the prompt or retry."),
        })],
      });
      setIsPromptLoading(false);
    }
  };

  /** Sends the composer prompt text to submitPrompt after validation. */
  const handleSendPrompt = async () => {
    const nextPrompt = promptText.trim();
    if (!nextPrompt) return;
    setSelectedPromptKey(null);
    submitPrompt(nextPrompt, "new");
  };

  /** Loads a session and resolves all prompts when a sidebar card is clicked. @param {string} nextSessionId - session id; @param {string} promptId - selected prompt id */
  const loadPromptResult = async (nextSessionId, promptId, currentStatus) => {
    const loadRequestId = activeLoadRequestRef.current + 1;
    activeLoadRequestRef.current = loadRequestId;
    const isCurrentLoadRequest = () => activeLoadRequestRef.current === loadRequestId;

    const controller = setNewAbortController();
    setActiveSessionId(nextSessionId);
    setSelectedPromptKey(buildPromptKey(nextSessionId, promptId));
    setIsResultView(true);
    setIsPromptLoading(true);

    const session = sessions.find((item) => item.session_id === nextSessionId);
    const selectedPrompt = session?.prompts?.find(
      (item) => String(item.prompt_id) === String(promptId),
    );
    const previousStatus = normalizePromptStatus(currentStatus ?? selectedPrompt?.status);
    const baseSidebarPrompt = {
      prompt_id: promptId,
      prompt: selectedPrompt?.prompt || "",
      timestamp: selectedPrompt?.timestamp || moment().format(DEFAULT_DATE_FORMAT),
      user_name: selectedPrompt?.user_name || session?.user_name || "",
      status: selectedPrompt?.status || "pending",
    };
    setSessionResult({
      sessionId: nextSessionId,
      sessionTitle: session?.session_title || "",
      prompts: [buildPromptResultItem(baseSidebarPrompt)],
    });

    const response = await getMLAnalyticsSession(nextSessionId, controller.signal);
    if (!isMountedRef.current || !isCurrentLoadRequest()) return;

    if (response.canceled || controller.signal.aborted) {
      if (isCurrentLoadRequest()) {
        setIsPromptLoading(false);
      }
      return;
    }

    if (!response.success) {
      const promptError = buildPromptError(response, "SESSION_LOAD_ERROR");
      setSessionResult({
        sessionId: nextSessionId,
        sessionTitle: session?.session_title || "",
        prompts: [buildPromptResultItem({
          ...baseSidebarPrompt,
          status: normalizePromptStatus(selectedPrompt?.status) || "failed",
          error: promptError,
        })],
      });
      setIsPromptLoading(false);
      toast.error(promptError?.error_message);
      return;
    }

    const resolvedSessionId = response.session_id || nextSessionId;
    const sessionPrompts = response.prompts || [];
    const sessionPrompt = promptId
      ? sessionPrompts.find((prompt) => String(prompt.prompt_id) === String(promptId))
      : sessionPrompts[0];

    if (promptId && !sessionPrompt) {
      setSessionResult({
        sessionId: resolvedSessionId,
        sessionTitle: response.session_title || "",
        prompts: [buildPromptErrorState(
          baseSidebarPrompt,
          "Unable to find the selected prompt.",
          "NOT_FOUND",
        )],
      });
      setIsPromptLoading(false);
      return;
    }

    let selectedResolvedPrompt;
    try {
      selectedResolvedPrompt = await resolvePromptResult(resolvedSessionId, sessionPrompt, controller.signal);
    } catch (error) {
      if (!isMountedRef.current || !isCurrentLoadRequest() || error?.canceled || controller.signal.aborted) {
        return;
      }
      selectedResolvedPrompt = buildPromptResultItem({
        prompt_id: sessionPrompt.prompt_id,
        prompt: sessionPrompt.prompt || "",
        timestamp: sessionPrompt.timestamp || null,
        user_name: sessionPrompt.user_name || "",
        status: normalizePromptStatus(sessionPrompt.status),
        error: buildFileLoadError(error.message || "Unable to load result files."),
      });
    }

    if (!isMountedRef.current || !isCurrentLoadRequest() || controller.signal.aborted) return;

    if (selectedResolvedPrompt && previousStatus === "pending" && selectedResolvedPrompt.status === "done") {
      await refetchSidebarPrompts();
      if (!isMountedRef.current || !isCurrentLoadRequest()) return;
      setSelectedPromptKey(buildPromptKey(resolvedSessionId, selectedResolvedPrompt.prompt_id));
    }

    setSessionResult({
      sessionId: resolvedSessionId,
      sessionTitle: response.session_title || "",
      prompts: selectedResolvedPrompt ? [selectedResolvedPrompt] : [],
    });
    setIsPromptLoading(false);
  };

  /** Resets workspace to composer view for a new prompt. @param {string} [nextSessionId] - optional new session id from the page */
  const handleNewPrompt = useCallback((nextSessionId) => {
    if (abortController.current) {
      abortController.current.abort();
    }
    activeLoadRequestRef.current += 1;
    if (nextSessionId) {
      setActiveSessionId(nextSessionId);
    }
    setSelectedPromptKey(null);
    setPromptText("");
    setIsResultView(false);
    setIsPromptLoading(false);
    setSessionResult(emptySessionResult);
  }, []); // eslint-disable-line

  useImperativeHandle(ref, () => ({
    startNewPrompt: handleNewPrompt,
  }), [handleNewPrompt]);

  useEffect(() => () => {
    isMountedRef.current = false;
    if (abortController.current) {
      abortController.current.abort();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="AIAnalyticsPromptWorkspace">
      <div className={`AIAnalyticsPromptWorkspace__Main${isResultView ? " AIAnalyticsPromptWorkspace__Main--Result" : ""}`}>
        {isResultView ? (
          <AIAnalyticsPromptResultPanel
            sessionTitle={sessionResult.sessionTitle}
            prompts={sessionResult.prompts}
            isPromptLoading={isPromptLoading}
            onRetry={(_promptId, prompt) => submitPrompt(prompt, "retry")}
            onReloadCurrentPrompt={(promptId, currentStatus) => loadPromptResult(sessionResult.sessionId, promptId, currentStatus)}
          />
        ) : (
          <AIAnalyticsPromptComposer
            promptText={promptText}
            onPromptChange={setPromptText}
            onSendPrompt={handleSendPrompt}
          />
        )}
      </div>
      <AIAnalyticsPromptSidebar
        sessions={sessions}
        isLoading={isSessionsLoading}
        selectedPromptKey={selectedPromptKey}
        onPromptSelect={loadPromptResult}
      />
    </div>
  );
}

export default forwardRef(AIAnalyticsPromptWorkspace);
